import { conducaoIaVisivel, isTerminalConversationStatus } from '@lume/shared';

import type { ChannelKind, ConversationStatus, OperationModeKind } from '@lume/shared';

/**
 * Labels PT-BR para enums canônicos. Mantém i18n inline aqui (Fatia 3
 * simples); migrar para i18next quando ≥ 2 idiomas.
 */

const STATUS_LABEL: Record<ConversationStatus, string> = {
  new: 'Nova',
  assigned: 'Atribuída',
  'in-progress': 'Em atendimento',
  'awaiting-customer': 'Aguardando cliente',
  'awaiting-internal-action': 'Aguardando interno',
  'awaiting-temporal': 'Aguardando prazo',
  paused: 'Pausada',
  closed: 'Encerrada',
  reopened: 'Reaberta',
  // Fatia F (ADR-054) — estados canônicos do lifecycle.
  'awaiting-attendant': 'Aguardando você',
  resolved: 'Resolvida',
  'closed-unresolved': 'Encerrada (não resolvida)',
  archived: 'Arquivada',
};

const STATUS_TONE: Record<ConversationStatus, 'neutral' | 'attention' | 'positive' | 'muted'> = {
  new: 'attention',
  assigned: 'neutral',
  'in-progress': 'neutral',
  'awaiting-customer': 'muted',
  'awaiting-internal-action': 'attention',
  'awaiting-temporal': 'muted',
  paused: 'muted',
  closed: 'positive',
  reopened: 'attention',
  // Fatia F (ADR-054) — estados canônicos do lifecycle.
  'awaiting-attendant': 'attention',
  resolved: 'positive',
  'closed-unresolved': 'muted',
  archived: 'muted',
};

const CHANNEL_LABEL: Record<ChannelKind, string> = {
  'whatsapp-cloud-api': 'WhatsApp',
  webchat: 'Webchat',
  email: 'Email',
  telegram: 'Telegram',
  'instagram-dm': 'Instagram',
  simulador: 'Simulador',
};

const MODE_LABEL: Record<OperationModeKind, string> = {
  H_a: 'Você dirige',
  H_b: 'Modo híbrido',
  H_c: 'IA dirigindo',
  // ADR-030 — delegação manual: o atendente delegou a conversa à IA.
  H_d: 'Delegada à IA',
};

const MODE_TONE: Record<OperationModeKind, 'human' | 'hybrid' | 'ai'> = {
  H_a: 'human',
  H_b: 'hybrid',
  H_c: 'ai',
  H_d: 'ai',
};

/**
 * Estados terminais canônicos (ADR-054 Fatia F) + legado `closed`, e a regra do
 * banner de condução da IA. Ambos MORAM em `@lume/shared`
 * (`conversations/terminal-status.ts`) desde 15/Ago/2026 — o app móvel virou o
 * consumidor que faltava, e a lista já estava copiada em 4 lugares.
 *
 * Os dois nomes seguem exportados daqui porque são o idioma local: `format.ts` é
 * onde os ~20 chamadores deste app os procuram, e trocar o import de todos eles
 * por uma mudança de domicílio seria churn sem leitor.
 *
 * História que os dois carregam, e que a promoção não pode apagar:
 * - `isTerminalStatus` nasceu de uma divergência real — o botão Encerrar usava o
 *   set canônico (PR #174) e o Composer só o literal `closed`, o que deixava o
 *   composer ativo depois de encerrar (status real `resolved`).
 * - `conducaoIaVisivel` nasceu do banner aparecendo em conversa ENCERRADA, visto
 *   pelo TL num episódio antigo em 13/Ago/2026: o modo de operação fica gravado
 *   na conversa e não se apaga quando ela fecha. Foi extraída da página porque
 *   regra escondida em página sem teste é regra que ninguém falsifica.
 */
export { conducaoIaVisivel };
export { isTerminalConversationStatus as isTerminalStatus };

/**
 * A conversa está sendo CONSULTADA (leitura de um episódio antigo pelo fio)?
 *
 * Duas condições, e as duas são necessárias:
 * - `voltarAoFio` diz **como se chegou** — veio do fio, e para onde voltar;
 * - o status diz **o que a conversa é**.
 *
 * A primeira versão exigia só o parâmetro, e o TL mediu o resultado na tela:
 * estando num episódio antigo e clicando na conversa VIVA da trilha, a navegação
 * levava o parâmetro junto — e a conversa ativa abria rotulada «somente leitura»,
 * com a trilha escondida. Consulta virava função de por onde se passou, não do
 * que se está olhando.
 *
 * Com o status na conta a regra se corrige sozinha: navegar para uma conversa
 * viva nunca entra em consulta, venha o parâmetro ou não.
 */
export function modoConsulta(
  voltarAoFio: string | null,
  status: ConversationStatus | undefined,
): boolean {
  if (voltarAoFio === null || voltarAoFio === '') return false;
  if (status === undefined) return false;
  return isTerminalConversationStatus(status);
}

/**
 * Terminal REABRÍVEL — os 3 estados de fechamento, sem `archived` (08/Ago/2026).
 *
 * `POST /conversations/:id/reopen` executa `reopenSame`, que exige `isClosing` no engine;
 * `archived` é final por desenho — a matriz de transição do ADR-054 o lista como estado que
 * não transiciona, e o inbound de um cliente numa conversa arquivada cria conversa NOVA em
 * vez de reabrir (`findOrCreateActiveConversation`). O header oferecia "Reabrir conversa"
 * para os 4 terminais, então em `archived` o botão era beco sem saída: abria o modal, o
 * operador confirmava e o backend devolvia «Conversa não está fechada (current=archived)».
 * Medido ao vivo pelo TL em 08/Ago/2026.
 *
 * Nota de produto (PENDENTE TL): o caminho adiante numa conversa arquivada é iniciar uma
 * conversa NOVA com o cliente — hoje o header não oferece isso, só deixa de mentir.
 */
const REOPENABLE_STATUSES: readonly ConversationStatus[] = [
  'closed',
  'resolved',
  'closed-unresolved',
];

export function isReopenableStatus(status: ConversationStatus): boolean {
  return REOPENABLE_STATUSES.includes(status);
}

export function statusLabel(status: ConversationStatus): string {
  return STATUS_LABEL[status];
}

export function statusTone(
  status: ConversationStatus,
): 'neutral' | 'attention' | 'positive' | 'muted' {
  return STATUS_TONE[status];
}

export function channelLabel(channel: ChannelKind): string {
  return CHANNEL_LABEL[channel];
}

export function modeLabel(mode: OperationModeKind): string {
  return MODE_LABEL[mode];
}

export function modeTone(mode: OperationModeKind): 'human' | 'hybrid' | 'ai' {
  return MODE_TONE[mode];
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const first = parts[0];
    return first ? first.charAt(0).toUpperCase() : '?';
  }
  const first = parts[0];
  const last = parts[parts.length - 1];
  const f = first?.charAt(0).toUpperCase() ?? '';
  const l = last?.charAt(0).toUpperCase() ?? '';
  return `${f}${l}` || '?';
}

/**
 * Formato relativo simples — "agora", "há 5min", "há 2h", "há 3d".
 * Sem dependência adicional (date-fns/dayjs) para evitar bloat.
 */
/**
 * Hora do balão de mensagem (decisão do TL, 13/Ago).
 *
 * `relativeTime` serve à janela VIVA («há quanto tempo o cliente espera?»). O
 * fio contínuo tornou dominante a leitura de HISTÓRICO («quando exatamente
 * isso aconteceu?») — e ali o relativo apaga o dado mais operacional que
 * existe: o INTERVALO entre as falas. Pior, acima de 7 dias ele caía em data
 * seca SEM hora, então duas mensagens separadas por 6 horas ficavam idênticas.
 *
 * Regra: relativo só abaixo de 1 minuto; daí em diante, hora absoluta. A data
 * não entra no balão — quem separa os dias é a divisória de dia
 * (`dayDividerLabel`), que também evita repetir a data em cada balão.
 */
export function messageTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const diffSec = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (diffSec >= 0 && diffSec < 30) return 'agora';
  if (diffSec >= 0 && diffSec < 60) return `${diffSec.toString()}s`;
  return then.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Data + hora completas — o que o hover do balão conta (o texto só tem a hora). */
export function fullTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Chave do dia CIVIL (local) — duas horas do mesmo dia colapsam numa divisória. */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear().toString()}-${(d.getMonth() + 1).toString()}-${d.getDate().toString()}`;
}

/** Rótulo da divisória de dia: «Hoje» · «Ontem» · «2 de abril» · com ano se diferir. */
export function dayDividerLabel(iso: string, now: Date = new Date()): string {
  if (dayKey(iso) === dayKey(now.toISOString())) return 'Hoje';
  const ontem = new Date(now.getTime());
  ontem.setDate(ontem.getDate() - 1);
  if (dayKey(iso) === dayKey(ontem.toISOString())) return 'Ontem';

  const d = new Date(iso);
  const mesmoAno = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    ...(mesmoAno ? {} : { year: 'numeric' }),
  });
}

export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 30) return 'agora';
  if (diffSec < 60) return `${diffSec.toString()}s`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin.toString()}min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `há ${diffHr.toString()}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `há ${diffDay.toString()}d`;
  return then.toLocaleDateString('pt-BR');
}
